import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiSend, FiCheckCircle, FiXCircle, FiArrowUpRight, FiTarget } from 'react-icons/fi';

/* ═══ CONQUER Cyan Theme ═══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  dark: '#0A1628',
  muted: '#64748B',
};

const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email', rcs: 'RCS',
  newsletter: 'Newsletter', push_notification: 'Push', voice_call: 'Voice',
  status_messaging: 'Status', custom: 'Custom'
};

/* ═══ Animated Counter ═══ */
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const numVal = typeof value === 'string' ? parseInt(value.replace(/,/g, ''), 10) : value;
  useEffect(() => {
    if (isNaN(numVal) || numVal === 0) { setDisplay(0); return; }
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * numVal));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [numVal, duration]);
  return <>{display.toLocaleString()}</>;
};

/* ═══ Section Header ═══ */
const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      {Icon && (
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: `linear-gradient(135deg, ${THEME.primary}20, ${THEME.accent}30)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${THEME.primary}30`,
        }}>
          <Icon size={18} style={{ color: THEME.primary }} />
        </div>
      )}
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, margin: 0, fontFamily: 'Poppins' }}>{title}</h3>
        {subtitle && <p style={{ fontSize: 12, color: THEME.muted, margin: '2px 0 0', fontWeight: 500 }}>{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

/* ═══ Stat Card with Gradient ═══ */
const StatCard = ({ icon: Icon, value, label, color, gradient, delay = 0 }) => (
  <div style={{
    background: '#fff', borderRadius: 20, padding: '24px 20px',
    position: 'relative', overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.04)',
    animation: `slideUp 0.5s ease-out ${delay}s both`,
  }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 4,
      background: gradient || `linear-gradient(90deg, ${color}, ${color}80)`,
    }} />
    <div style={{
      position: 'absolute', top: -30, right: -30, width: 100, height: 100,
      borderRadius: '50%', background: `${color}08`, filter: 'blur(20px)',
    }} />
    <div style={{
      width: 48, height: 48, borderRadius: 14,
      background: `linear-gradient(135deg, ${color}15, ${color}25)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 16, border: `1px solid ${color}20`,
    }}>
      <Icon size={22} style={{ color }} />
    </div>
    <div style={{ fontSize: 32, fontWeight: 800, color: THEME.dark, fontFamily: 'Poppins', lineHeight: 1 }}>
      <AnimatedNumber value={value} duration={1000 + delay * 200} />
    </div>
    <div style={{ fontSize: 13, color: THEME.muted, marginTop: 6, fontWeight: 500 }}>{label}</div>
    <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
  </div>
);

export default function UserDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/user/dashboard').then(({ data }) => { setData(data); setLoading(false); })
      .catch(() => { toast.error('Failed to load dashboard'); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const stats = data?.stats || {};
  const recentCampaigns = data?.recentCampaigns || [];

  const totalMessages = (stats.totalSent || 0) + (stats.totalFailed || 0) + (stats.totalPending || 0);
  const successRate = totalMessages > 0 ? Math.round((stats.totalSent / totalMessages) * 100) : 0;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Mission Control</h1>
          <p className="page-subtitle">Your broadcast performance overview</p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 20px', borderRadius: 16,
          background: successRate >= 80 
            ? `linear-gradient(135deg, ${THEME.success}15, ${THEME.success}08)`
            : successRate >= 50 
              ? `linear-gradient(135deg, ${THEME.warning}15, ${THEME.warning}08)`
              : `linear-gradient(135deg, ${THEME.danger}15, ${THEME.danger}08)`,
          border: `1px solid ${successRate >= 80 ? THEME.success : successRate >= 50 ? THEME.warning : THEME.danger}25`,
        }}>
          <FiArrowUpRight size={18} style={{ color: successRate >= 80 ? THEME.success : successRate >= 50 ? THEME.warning : THEME.danger }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: successRate >= 80 ? THEME.success : successRate >= 50 ? THEME.warning : THEME.danger }}>
            {successRate}% Success Rate
          </span>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
        <StatCard icon={FiSend} value={stats.totalCampaigns || 0} label="My Outreach" color={THEME.primary} delay={0} />
        <StatCard icon={FiTarget} value={stats.totalAssigned || 0} label="Total Assigned" color="#8B5CF6" delay={0.1} />
        <StatCard icon={FiCheckCircle} value={stats.totalSent || 0} label="Delivered" color={THEME.success} delay={0.2} />
        <StatCard icon={FiXCircle} value={stats.totalFailed || 0} label="Failed" color={THEME.danger} delay={0.3} />
      </div>

      {/* Recent Campaigns - Card Grid */}
      {recentCampaigns.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: `linear-gradient(135deg, ${THEME.primary}20, ${THEME.accent}30)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${THEME.primary}30`,
              }}>
                <FiSend size={20} style={{ color: THEME.primary }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: THEME.dark }}>Recent Outreach</h3>
                <p style={{ margin: 0, fontSize: 13, color: THEME.muted }}>Your latest outreach data</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/user/campaigns')} 
              style={{ 
                fontSize: 13, padding: '10px 20px', borderRadius: 12,
                background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
                color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer',
                boxShadow: `0 4px 14px ${THEME.primary}40`,
                transition: 'all 0.3s ease',
              }}
            >
              View All →
            </button>
          </div>
          
          {/* Campaign Cards - Each row has card + pie chart */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {recentCampaigns.map((c, i) => {
              const total = (c.mySent || 0) + (c.myFailed || 0) + (c.myPending || 0);
              const sentPct = total > 0 ? (c.mySent || 0) / total * 100 : 0;
              const failedPct = total > 0 ? (c.myFailed || 0) / total * 100 : 0;
              const pendingPct = total > 0 ? (c.myPending || 0) / total * 100 : 0;
              
              return (
                <div 
                  key={c._id}
                  style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}
                >
                  {/* Campaign Card */}
                  <div 
                    onClick={() => navigate(`/user/campaigns/${c._id}`)}
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 20,
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      flex: 1,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 8px 20px ${THEME.primary}15`;
                      e.currentTarget.style.borderColor = THEME.primary;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    {/* Top accent bar */}
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                      background: c.status === 'active' 
                        ? `linear-gradient(90deg, ${THEME.success}, ${THEME.success}80)`
                        : c.status === 'completed' 
                          ? `linear-gradient(90deg, ${THEME.primary}, ${THEME.secondary})`
                          : `linear-gradient(90deg, ${THEME.warning}, ${THEME.warning}80)`,
                    }} />

                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          margin: 0, fontSize: 18, fontWeight: 700, color: THEME.primary,
                          marginBottom: 6, lineHeight: 1.3,
                        }}>{c.name}</h4>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: `${THEME.secondary}20`, color: THEME.primary,
                          border: `1px solid ${THEME.secondary}40`,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                        }}>{TYPE_LABELS[c.type] || c.type}</span>
                      </div>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        background: c.status === 'active' ? `${THEME.success}15` : c.status === 'completed' ? `${THEME.primary}15` : `${THEME.warning}15`,
                        color: c.status === 'active' ? THEME.success : c.status === 'completed' ? THEME.primary : THEME.warning,
                        border: `1px solid ${c.status === 'active' ? THEME.success : c.status === 'completed' ? THEME.primary : THEME.warning}30`,
                      }}>{c.status}</span>
                    </div>

                    {/* Stats Grid */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: 12,
                    }}>
                      {/* Assigned */}
                      <div style={{
                        background: '#f8fafc',
                        borderRadius: 12,
                        padding: 14,
                        textAlign: 'center',
                      }}>
                        <div style={{ fontSize: 10, color: THEME.muted, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Assigned</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: THEME.dark }}>{c.myAssigned?.toLocaleString() || 0}</div>
                      </div>
                      
                      {/* Sent */}
                      <div style={{
                        background: `${THEME.success}08`,
                        borderRadius: 12,
                        padding: 14,
                        textAlign: 'center',
                        border: `1px solid ${THEME.success}20`,
                      }}>
                        <div style={{ fontSize: 10, color: THEME.success, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sent</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: THEME.success }}>{c.mySent?.toLocaleString() || 0}</div>
                      </div>
                      
                      {/* Failed */}
                      <div style={{
                        background: `${THEME.danger}08`,
                        borderRadius: 12,
                        padding: 14,
                        textAlign: 'center',
                        border: `1px solid ${THEME.danger}20`,
                      }}>
                        <div style={{ fontSize: 10, color: THEME.danger, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Failed</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: THEME.danger }}>{c.myFailed?.toLocaleString() || 0}</div>
                      </div>
                      
                      {/* Pending */}
                      <div style={{
                        background: `${THEME.warning}08`,
                        borderRadius: 12,
                        padding: 14,
                        textAlign: 'center',
                        border: `1px solid ${THEME.warning}20`,
                      }}>
                        <div style={{ fontSize: 10, color: THEME.warning, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Pending</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: THEME.warning }}>{c.myPending?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                  </div>

                  {/* Pie Chart Card - Beside the campaign card */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 16,
                    padding: 20,
                    border: '1px solid #e2e8f0',
                    width: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {total === 0 ? (
                      <div style={{ 
                        width: 120, height: 120, borderRadius: '50%', 
                        background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ fontSize: 12, color: THEME.muted }}>No Data</span>
                      </div>
                    ) : (
                      <>
                        <div style={{ position: 'relative', width: 120, height: 120 }}>
                          <svg width="120" height="120" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="48" fill="none" stroke="#f1f5f9" strokeWidth="20" />
                            <circle cx="60" cy="60" r="48" fill="none" stroke={THEME.success} strokeWidth="20"
                              strokeDasharray={`${sentPct * 3.01} 301`} strokeDashoffset="0"
                              style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                            <circle cx="60" cy="60" r="48" fill="none" stroke={THEME.danger} strokeWidth="20"
                              strokeDasharray={`${failedPct * 3.01} 301`} strokeDashoffset={`${-sentPct * 3.01}`}
                              style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                            <circle cx="60" cy="60" r="48" fill="none" stroke={THEME.warning} strokeWidth="20"
                              strokeDasharray={`${pendingPct * 3.01} 301`} strokeDashoffset={`${-(sentPct + failedPct) * 3.01}`}
                              style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                          </svg>
                          <div style={{
                            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            <span style={{ fontSize: 22, fontWeight: 800, color: THEME.dark }}>{Math.round(sentPct)}%</span>
                            <span style={{ fontSize: 9, color: THEME.muted, fontWeight: 600, textTransform: 'uppercase' }}>Sent</span>
                          </div>
                        </div>
                        {/* Legend */}
                        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: THEME.success }} />
                            <span style={{ fontSize: 10, color: THEME.muted }}>Sent</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: THEME.danger }} />
                            <span style={{ fontSize: 10, color: THEME.muted }}>Failed</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: THEME.warning }} />
                            <span style={{ fontSize: 10, color: THEME.muted }}>Pending</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
